import type { ReactNode } from 'react';
import { Link } from 'react-router';

import config from '@/config';
import { ERROR_CODES, errorMessages } from '@/core/api';
import { getDefaultErrorMessage } from '@/core/apiErrorMessages';

/** Where an expert actually sets their country. The profile Location form is
 *  the only place that writes `country_iso2_code`; nothing in expert signup or
 *  onboarding collects it. */
export const LOCATION_SETTINGS_PATH = '/settings/location';
/** And where the phone lives: `Communication` renders `CommunicationForm` with
 *  `editPhone`, so this is the form, not a preferences page. */
export const COMMUNICATION_SETTINGS_PATH = '/settings/communication';
const EMAIL_SETTINGS_PATH = '/settings/emails';

/**
 * A settings link that *reads* as the full address while still navigating
 * client-side.
 *
 * The visible text is absolute and environment-correct (`config.webUrl`), so an
 * expert who would rather go and find the page themselves — or who is reading
 * this over someone's shoulder — has the actual address rather than a bare
 * "here". The `to` stays relative so the click is a route change, not a full
 * page load that drops them back through the router.
 *
 * Both classes are load-bearing against `src/theme/index.css`, which styles
 * every anchor globally:
 *
 * - `text-current` because `a { color: var(--primaryLight) }` declares a colour
 *   *on the anchor*, and an explicit declaration beats an inherited one — a
 *   link with no colour class renders teal inside a red message. `color:
 *   currentColor` on `color` itself computes to `inherit`, and the utility's
 *   (0,1,0) outranks the global rule's (0,0,1), so this re-enables inheritance
 *   rather than restating the paragraph's palette. Verified in the running app:
 *   link and paragraph both compute rgb(215,43,43) light, rgb(244,117,117) dark.
 *   (`text-inherit` does not exist here — `tailwind.config.ts` sets `colors`
 *   rather than `extend.colors`, so the default palette is replaced and only
 *   `transparent` and `current` survive from it.)
 * - `hover:underline` because `a:hover { text-decoration: none }` is (0,1,1)
 *   and bare `.underline` is (0,1,0), so the underline would vanish on hover —
 *   the one affordance this link has. The variant compiles to (0,2,0) and wins.
 */
function SettingsLink({ path }: { path: string }): JSX.Element {
  return (
    <Link to={path} className="text-current underline hover:underline">
      {`${config.webUrl}${path}`}
    </Link>
  );
}

/**
 * Where to send an expert for the field Stripe refused.
 *
 * The api names the field in prose but not in the code, so the destination is
 * matched on that prose. Deliberately a *safe* match: anything unrecognised
 * falls through to no link at all, so wording drift costs the link rather than
 * sending someone to the wrong form. Keys mirror `stripeAccountParamLabels` in
 * `payout/payout_service.go`; its two name labels are absent on purpose, since
 * a name is edited from a dialog on the profile page and has no `/settings/*`
 * route to point at.
 */
const REJECTED_FIELD_DESTINATIONS: readonly (readonly [RegExp, string])[] = [
  [/phone number/i, COMMUNICATION_SETTINGS_PATH],
  [/email address/i, EMAIL_SETTINGS_PATH],
  [/country/i, LOCATION_SETTINGS_PATH],
];

/**
 * Copy for a failed **Set up payouts** click, keyed on the api's error code.
 *
 * Every branch here exists because the api used to raise these uncoded, so
 * `getDefaultErrorMessage` had nothing to return and the expert got "try again,
 * or contact support" for conditions where retrying can never succeed and
 * support is not needed (ENG-5723).
 */
export function connectSetupError(code: string | undefined, err: unknown): ReactNode {
  // The one case the expert can fix in seconds, and the default state of every
  // new expert — so it gets the address of the form, not a path to go and find.
  if (code === ERROR_CODES.PAYOUT_COUNTRY_REQUIRED) {
    return (
      <>
        {'Add your country before setting up payouts. Set it at '}
        <SettingsLink path={LOCATION_SETTINGS_PATH} />
        {', then try again.'}
      </>
    );
  }

  if (code === ERROR_CODES.PAYOUT_SETUP_REJECTED) {
    // The api interpolates the refused field into this message, which is the
    // whole value of the code; the static default names no field.
    //
    // Guarded on there being exactly one error, because `firstErrorCode` skips
    // uncoded entries while `errorMessages` preserves every index — so on a
    // multi-error response the code and the message could come from different
    // errors, and the regex below would then pick a destination from the wrong
    // text. `createPayoutAccountURL` raises a single error today, so this is
    // the invariant made explicit rather than a bug being fixed.
    const messages = errorMessages(err);
    const [serverMessage] = messages;
    if (messages.length === 1 && serverMessage !== undefined && serverMessage.length > 0) {
      const destination = REJECTED_FIELD_DESTINATIONS.find(([pattern]) =>
        pattern.test(serverMessage)
      );
      if (destination === undefined) return serverMessage;
      // "Go to", not "Update it at": the api message already ends with "Update
      // it in your profile and try again", so a second instruction would say
      // the same thing twice. This points at the page that sentence means.
      return (
        <>
          {`${serverMessage} Go to `}
          <SettingsLink path={destination[1]} />
          {'.'}
        </>
      );
    }
  }

  return (
    getDefaultErrorMessage(code ?? '') ??
    'We could not open Stripe Connect setup. Please try again, or contact support.'
  );
}
